﻿import React, { useRef, useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  Modal,
  TouchableOpacity,
  Platform,
  ActivityIndicator,
  Dimensions,
} from 'react-native';
import { X, Share2, Copy } from 'lucide-react-native';
import { useTheme } from '@/contexts/ThemeContext';
import { useSubscription } from '@/hooks/useSubscription';
import { useUnits } from '@/lib/useUnits';
import WorkoutShareCard, { type WorkoutExercise } from './WorkoutShareCard';
import { shareCard } from '@/lib/shareCard';
import * as Clipboard from 'expo-clipboard';

interface WorkoutShareModalProps {
  visible: boolean;
  onClose: () => void;
  username: string;
  gymName: string;
  exercises: WorkoutExercise[];
  rank?: number;
  strengthScore?: number;
}

const CARD_W = 1080;

export default function WorkoutShareModal({
  visible,
  onClose,
  username,
  gymName,
  exercises,
  rank,
  strengthScore,
}: WorkoutShareModalProps) {
  const { theme } = useTheme();
  const { isProPlus } = useSubscription();
  const showWatermark = !isProPlus;
  const { userUnits } = useUnits();
  const cardRef = useRef<View>(null);
  const [sharing, setSharing] = useState(false);
  const [cardH, setCardH] = useState(1080);
  const [storyMode, setStoryMode] = useState(false);
  const [copied, setCopied] = useState(false);

  // Calculate scale to fit preview area
  const screenW = Dimensions.get('window').width;
  const previewMaxW = Math.min(screenW - 80, 360);
  const previewMaxH = 520;
  const scaleW = previewMaxW / CARD_W;
  const scaleH = previewMaxH / cardH;
  const scale = Math.min(scaleW, scaleH);
  const previewW = CARD_W * scale;
  const previewH = cardH * scale;

  const buildShareText = () => {
    const realGym = gymName && gymName !== 'My Gym' && gymName !== 'Independent' ? gymName : null;
    const exLines = exercises
      .slice(0, 5)
      .map((ex) => {
        const weightStr = ex.weight > 0 ? `${ex.weight} ${userUnits}` : 'BW';
        const setsStr = ex.sets && ex.sets > 1 ? `${ex.sets}×` : '';
        const prTag = ex.isPR ? ' ★ PR' : '';
        return `• ${ex.name} — ${setsStr}${weightStr} × ${ex.reps}${prTag}`;
      })
      .join('\n');
    const more = exercises.length > 5 ? `\n+ ${exercises.length - 5} more exercises` : '';
    const gymLine = realGym ? ` at ${realGym}` : '';
    const rankLine = rank ? ` · Gym Rank #${rank}` : '';
    const scoreLine = strengthScore && strengthScore > 0 ? `\nStrength Score: ${strengthScore.toLocaleString()}` : '';
    const totalVol = exercises.reduce((s, e) => s + (e.weight * e.reps * (e.sets ?? 1)), 0);
    const volLine = totalVol > 0 ? `\nTotal Volume: ${totalVol.toLocaleString()} ${userUnits}` : '';
    return `💪 @${username} just logged a workout on DUEL${gymLine}${rankLine}${scoreLine}${volLine}\n\n${exLines}${more}\n\nChallenge me \u2192 douevenlift.com\n#DUEL #DoUEvenLift`;
  };

  const handleCopyText = async () => {
    const text = buildShareText();
    await Clipboard.setStringAsync(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 3000);
  };

  const handleShare = async () => {
    setSharing(true);
    try {
      // Copy workout summary to clipboard so it can be pasted into Facebook posts
      // (Facebook's share sheet often drops the message field — clipboard is a reliable fallback)
      const shareText = buildShareText();
      await Clipboard.setStringAsync(shareText);
      await shareCard(cardRef);
    } catch (e: any) {
      if (Platform.OS === 'web') {
        window.alert('Failed to share: ' + (e.message || e));
      }
    } finally {
      setSharing(false);
    }
  };

  return (
    <Modal visible={visible} animationType="slide" transparent onRequestClose={onClose}>
      <TouchableOpacity style={styles.overlay} activeOpacity={1} onPress={onClose}>
        <TouchableOpacity activeOpacity={1} onPress={(e) => e.stopPropagation()} style={{ width: '100%' }}>
        <View style={[styles.container, { backgroundColor: theme.colors.surface }]}>
          <View style={styles.header}>
            <Text style={[styles.title, { color: theme.colors.text }]}>Share Your Workout</Text>
            <TouchableOpacity onPress={onClose} style={styles.closeBtn}>
              <X size={24} color={theme.colors.textSecondary} />
            </TouchableOpacity>
          </View>

          {/* Scaled card preview */}
          <View style={[styles.previewContainer, { width: previewW, height: previewH }]}>
            <View
              onLayout={(e) => {
                const h = e.nativeEvent.layout.height;
                if (h > 0 && Math.abs(h - cardH) > 10) setCardH(h);
              }}
              style={{
                width: CARD_W,
                transform: [
                  { translateX: -(CARD_W * (1 - scale)) / 2 },
                  { translateY: -(cardH * (1 - scale)) / 2 },
                  { scale },
                ],
              }}
            >
              <WorkoutShareCard
                ref={cardRef}
                username={username}
                gymName={gymName}
                exercises={exercises}
                rank={rank}
                strengthScore={strengthScore}
                storyMode={storyMode}
                unit={userUnits}
                showWatermark={showWatermark}
              />
            </View>
          </View>

          {/* Format toggle */}
          <View style={styles.toggleRow}>
            <TouchableOpacity
              style={[styles.toggleBtn, !storyMode && styles.toggleActive]}
              onPress={() => setStoryMode(false)}
            >
              <Text style={[styles.toggleText, !storyMode && styles.toggleTextActive]}>Post</Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={[styles.toggleBtn, storyMode && styles.toggleActive]}
              onPress={() => setStoryMode(true)}
            >
              <Text style={[styles.toggleText, storyMode && styles.toggleTextActive]}>Story (9:16)</Text>
            </TouchableOpacity>
          </View>

          <TouchableOpacity
            style={styles.shareBtn}
            onPress={handleShare}
            disabled={sharing}
          >
            {sharing ? (
              <ActivityIndicator color="#fff" />
            ) : (
              <>
                <Share2 size={20} color="#fff" />
                <Text style={styles.shareBtnText}>Share Card</Text>
              </>
            )}
          </TouchableOpacity>

          {/* Clipboard copy button — fallback for Facebook which ignores message field */}
          <TouchableOpacity style={styles.copyBtn} onPress={handleCopyText}>
            <Copy size={16} color={copied ? '#B8972E' : '#4A4840'} />
            <Text style={[styles.copyBtnText, copied && styles.copyBtnTextActive]}>
              {copied ? 'Copied! Paste into your post' : 'Copy workout text'}
            </Text>
          </TouchableOpacity>

          {showWatermark && (
            <Text style={styles.watermarkHint}>
              Upgrade to Pro to remove the watermark
            </Text>
          )}
        </View>
        </TouchableOpacity>
      </TouchableOpacity>
    </Modal>
  );
}

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    justifyContent: 'flex-end',
    backgroundColor: 'rgba(0,0,0,0.9)',
  },
  container: {
    borderTopLeftRadius: 2,
    borderTopRightRadius: 2,
    padding: 20,
    alignItems: 'center',
    backgroundColor: '#0A0A0A',
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 16,
    width: '100%',
  },
  title: {
    fontSize: 16,
    fontFamily: 'Oswald_700Bold',
    letterSpacing: 2,
    color: '#E8E4DB',
    textTransform: 'uppercase' as const,
  },
  closeBtn: {
    padding: 4,
  },
  previewContainer: {
    overflow: 'hidden',
    borderRadius: 2,
    borderWidth: 2,
    borderColor: '#1A1A1A',
  },
  toggleRow: {
    flexDirection: 'row',
    gap: 8,
    marginTop: 12,
    width: '100%',
  },
  toggleBtn: {
    flex: 1,
    paddingVertical: 10,
    borderRadius: 0,
    alignItems: 'center',
    backgroundColor: '#0A0A0A',
    borderWidth: 2,
    borderColor: '#1A1A1A',
  },
  toggleActive: {
    backgroundColor: '#1A1A1A',
    borderColor: '#B8972E',
  },
  toggleText: {
    color: '#4A4840',
    fontSize: 13,
    fontFamily: 'BarlowCondensed_700Bold',
    letterSpacing: 1,
    textTransform: 'uppercase' as const,
  },
  toggleTextActive: {
    color: '#E8E4DB',
  },
  shareBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    paddingVertical: 14,
    borderRadius: 0,
    marginTop: 16,
    width: '100%',
    backgroundColor: '#B8972E',
    borderWidth: 2,
    borderColor: '#8A7122',
  },
  shareBtnText: {
    color: '#030303',
    fontSize: 15,
    fontFamily: 'Oswald_700Bold',
    letterSpacing: 2,
    textTransform: 'uppercase' as const,
  },
  copyBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    paddingVertical: 10,
    marginTop: 8,
    width: '100%',
    borderWidth: 1,
    borderColor: '#1A1A1A',
    borderRadius: 0,
  },
  copyBtnText: {
    color: '#4A4840',
    fontSize: 12,
    fontFamily: 'BarlowCondensed_700Bold',
    letterSpacing: 1,
    textTransform: 'uppercase' as const,
  },
  copyBtnTextActive: {
    color: '#B8972E',
  },
  watermarkHint: {
    color: '#4A4840',
    fontSize: 12,
    fontFamily: 'BarlowCondensed_700Bold',
    letterSpacing: 1,
    textAlign: 'center' as const,
    marginTop: 8,
  },
});

